import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import uuid from 'react-uuid';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes/currencyPageTypes';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { FundOwnershipDetail } from 'common/types/fundOwnership';
import { UseStoreValues } from 'common/types/store';
import { LedgerTable } from 'components';
import { LedgerDialog } from 'components/Dialogs';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { cellsParser, reverseCellsParser } from 'components/LedgerTable/utilities/ledgerParser';
import FundOwnershipContext from 'context/FundOwnershipContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { AMOUNT_ALIAS, DISTRIBUTION_DATE_ALIAS } from 'pages/CapTable/fund-ownership/components/shares-ledger/constants';
import useExchangeRateMap from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useExchangeRateMap';
import useFundOwnershipFundCell from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useFundOwnershipFundCell';
import { applyPreFxAnnotation } from 'pages/CapTable/fund-ownership/components/shares-ledger/utilities/applyPreFxAnnotation';
import { FundOwnershipCell } from 'pages/CapTable/fund-ownership/types';
import { reverseParser } from 'pages/CapTable/fund-ownership/utilities';
import updateAcquisitionsFromDialogToCells from 'pages/CapTable/fund-ownership/utilities/updateAcquisitionsFromDialogToCells';
import { useTableValidation } from 'services/hooks';
import slashedToIsoFormat from 'utilities/slashedToIsoFormat';
import {
  CashDistribution,
  CashDistributionLedgerAcquisition,
  CashDistributionLedgerCell,
  CashDistributionLedgerProps,
} from './CashDistributionLedger.props';
import colConfig from './colConfig';

const emptyRow = { purchase_date: null, distribution_date: null, amount: null };

const MultipleInvestmentDates = ({ cell, closeDialog }: CashDistributionLedgerProps) => {
  const { cells, columns, onCellsChanged } = useFeatureSpreadsheet();

  const existingAcquisitions = useMemo(
    () =>
      cell?.acquisitions
        ? cell?.acquisitions.map(acquisition => {
          const tempRef = uuid();
          const cashDistributions = (acquisition.cash_distributions || []).map(distr => ({
            ...distr,
            acquisition_ref: tempRef,
            distribution_ref: uuid(),
          }));
          return {
            ...acquisition,
            cash_distributions: cashDistributions,
            deleted_cash_distributions: acquisition.deleted_cash_distributions ?? [],
            acquisition_ref: tempRef,
          } as CashDistributionLedgerAcquisition;
        })
        : [],
    [cell?.acquisitions]
  );

  const startingDistributions: CashDistribution[] = useMemo(() => {
    if (existingAcquisitions && existingAcquisitions.length > 0) {
      const distributions = existingAcquisitions.map(acquisition => acquisition.cash_distributions).flat();
      return distributions.map(distr => ({
        ...distr,
        purchase_date: existingAcquisitions.find(acq => acq.acquisition_ref === distr.acquisition_ref)?.purchase_date,
        id: distr.id ?? 0,
      }));
    }
    return [];
  }, [existingAcquisitions]);

  const [rows, setRows] = useState<CashDistribution[]>(startingDistributions);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [ledgerCells, setLedgerCells] = useState<{ [key: string]: ICell }>();

  // Live ledger-cells mirror so the Pre-FX preview can pull each row's
  // distribution_date dynamically from what the user typed in the cell,
  // instead of from the trailing `rows` state.
  const ledgerCellsRef = useRef(ledgerCells);
  useEffect(() => {
    ledgerCellsRef.current = ledgerCells;
  }, [ledgerCells]);
  const { validateTable } = useTableValidation();
  const { isDisabled, fundList, selectedMeasurementDate, spreadsheetTableData, fieldAttributes }
    = useContext(FundOwnershipContext);
  const [format] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE, units: REGULAR_UNIT });
  // Default Pre-FX source currency for distribution AMOUNT cells. The
  // company's captable_currency is the FOD-side default per spec.
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const defaultPreFxCurrency = companyInfo?.captable_currency as string | undefined;

  const disabledCondition = isDisabled || (existingAcquisitions && existingAcquisitions.length === 0);

  const fund = useFundOwnershipFundCell({ cells, cell: cell as FundOwnershipCell, fundList });

  const isDifferentCurrency = useMemo(
    () => !!(fund?.currency && fund.currency !== format.currency.code),
    [fund, format]
  );

  const dataSourceKey = useMemo(() => cell?.linkedCells?.values().next().value?.key, [cell?.linkedCells]) as string;

  // Reverse parse the cells
  const investmentDates = useMemo((): CashDistribution[] => {
    if (!isEmpty(ledgerCells) && !isEmpty(rows)) {
      return reverseCellsParser(rows, ledgerCells);
    }
    return [];
  }, [rows, ledgerCells]);

  const config = useMemo(() => colConfig({ acquisitions: existingAcquisitions }), [existingAcquisitions]);

  const { exchangeRateMap, linkedCellChanges } = useExchangeRateMap({
    fundCurrency: fund?.currency as string,
    format,
    shares: investmentDates,
    selectedMeasurementDate: selectedMeasurementDate!,
    isDifferentCurrency,
    cellKeys: [AMOUNT_ALIAS],
    dateProp: 'distribution_date',
  });

  const addRow = useCallback(() => {
    const oldestAcquisition = existingAcquisitions?.reduce(
      (acc, curr) => (acc.purchase_date < curr.purchase_date ? acc : curr),
      {} as CashDistributionLedgerAcquisition
    );
    const newRow = {
      ...emptyRow,
      purchase_date: oldestAcquisition?.purchase_date,
      distribution_ref: uuid(),
      acquisition_ref: oldestAcquisition?.acquisition_ref,
    };

    setRows([...investmentDates, newRow as CashDistribution]);
  }, [existingAcquisitions, investmentDates]);

  const deleteRow = (rowIndex: number) => {
    const tmpRows = [...rows];
    const removed = tmpRows.splice(rowIndex, 1)[0];
    if (removed.id) {
      // see which place the deleted distribution occupied in the acquisitions distributions
      const acquisition = existingAcquisitions?.find(acq => acq.acquisition_ref === removed.acquisition_ref);
      const distributionIndex = (acquisition?.cash_distributions || []).findIndex(
        distr => distr.distribution_ref === removed.distribution_ref
      );
      const itemToPush = acquisition?.cash_distributions[distributionIndex].id;
      if (itemToPush) {
        acquisition?.deleted_cash_distributions.push(itemToPush);
      }
    }
    setRows(tmpRows);
  };

  const save = () => {
    if (!ledgerCells) {
      return;
    }

    // validateTable
    const cellsToValidate = Object.entries(ledgerCells).map(([, ledgerCell]) => ledgerCell);
    const { areCellsValid, validatedCells } = validateTable({
      cellsToValidate,
      toggleAlert: false,
    });

    // Update ledger cells with validation results (error styling)
    if (validatedCells && validatedCells.length > 0) {
      const updatedLedgerCells = { ...ledgerCells };
      validatedCells.forEach(validatedCell => {
        if (validatedCell.key) {
          updatedLedgerCells[validatedCell.key] = validatedCell;
        }
      });
      setLedgerCells(updatedLedgerCells);
    }

    if (areCellsValid) {
      const tmpState = { ...cells };

      // Save array in the data source field
      const updatedDistributions = investmentDates.map(distr => ({
        ...distr,
        distribution_date: slashedToIsoFormat({ label: distr.distribution_date! }),
      })) as unknown as CashDistribution[];
      tmpState[dataSourceKey].value = updatedDistributions;

      const totalDistributions
        = investmentDates?.map(({ amount }) => Number(amount)).reduce((acc, curr) => acc + curr, 0) ?? 0;
      // Save the total distributions in the current field
      tmpState[cell?.key ?? ''].value = totalDistributions;

      // update the acquisitions.cash_distributions prop
      const updatedAcquisitions = existingAcquisitions?.map(acquisition => {
        const acquisitionDistributions = updatedDistributions.filter(
          distr => distr.acquisition_ref === acquisition.acquisition_ref
        );
        if (acquisition.id) {
          acquisitionDistributions.forEach(distr => {
            if (distr.id) {
              // eslint-disable-next-line no-param-reassign
              distr.acquisition = acquisition.id;
            }
          });
        }
        return { ...acquisition, cash_distributions: acquisitionDistributions };
      });
      updateAcquisitionsFromDialogToCells({
        fundOwnershipDetails: columns as unknown as FundOwnershipDetail[],
        reverseParsedAcquisitions: updatedAcquisitions,
        cell: cell as unknown as CashDistributionLedgerCell,
        startingAcquisitions: existingAcquisitions,
        deletedAcquisitions: cell?.deleted_acquisitions ?? [],
      });
      onCellsChanged([{ cell, value: totalDistributions.toString() }]);

      reverseParser({
        cells: tmpState,
        columns,
        tableData: spreadsheetTableData,
        fieldAttributes: fieldAttributes as unknown as Map<string, any>,
      });
      closeDialog?.();
    } else {
      setIsAlertVisible(true);
    }
  };

  // Parse cells
  useEffect(() => {
    if (Array.isArray(rows)) {
      const parsedCells = cellsParser(rows, config, cell) as { [key: string]: ICell };
      Object.values(parsedCells).forEach((cell: ICell) => {
        if (cell.alias === AMOUNT_ALIAS && isDifferentCurrency) {
          const objectIndex = `distribution_date${cell.rowNumber! + 1}`;
          // eslint-disable-next-line no-param-reassign
          (cell as any).relatedDate = parsedCells[objectIndex].value;
        }
      });
      // Attach Pre-FX annotation viewer to AMOUNT cells. The cell-key target
      // is the CashDistribution row, NOT the FOD-level cash_distribution
      // summary cell. Newly-added rows (no id yet) are skipped — annotations
      // require a server-side parent.
      const outCells: { [key: string]: ICell } = { ...parsedCells };
      Object.entries(parsedCells).forEach(([key, parsedCell]) => {
        if (parsedCell.alias === AMOUNT_ALIAS) {
          const distribution = rows[parsedCell.rowNumber!];
          const distRowNumber = parsedCell.rowNumber!;
          // Distribution date getter: read from the live ledgerCells map so
          // the preview sees a freshly-typed date even before the next
          // reverse-parse cycle.
          const getDistributionDate = (): string | undefined | null => {
            const dateCell = ledgerCellsRef.current?.[`${DISTRIBUTION_DATE_ALIAS}${distRowNumber + 1}`] as
              | { value?: string | null }
              | undefined;
            const fromCells = (dateCell?.value as string | null | undefined) ?? undefined;
            return fromCells ?? (distribution?.distribution_date as string | null | undefined) ?? undefined;
          };
          // Patch the host amount cell DIRECTLY in `ledgerCells` so other
          // live cell edits (distribution_date, purchase_date) aren't
          // wiped by a round-trip through stale `rows` state.
          const hostCellKey = parsedCell.key;
          const onHostPreview = hostCellKey
            ? (value: number | null) => {
              setLedgerCells(prev => ({
                ...(prev ?? {}),
                [hostCellKey]: {
                  ...((prev ?? {})[hostCellKey] ?? {}),
                  value,
                  expr: value === null ? '' : String(value),
                } as ICell,
              }));
            } : undefined;
          outCells[key] = applyPreFxAnnotation({
            cell: parsedCell,
            parentType: 'cashdistribution',
            parentId: distribution?.id,
            fieldAlias: AMOUNT_ALIAS,
            defaultCurrency: defaultPreFxCurrency,
            hostCurrency: defaultPreFxCurrency,
            transactionDate: getDistributionDate,
            onHostPreview,
          });
        }
      });
      setLedgerCells(outCells);
    }
  }, [rows, isDifferentCurrency, config, cell, defaultPreFxCurrency]);

  return (
    <LedgerDialog
      id="cash-distribution-ledger"
      title="Cash Distribution"
      onSave={save}
      onClose={closeDialog!}
      disabled={cell?.isDisabled}>
      {ledgerCells && (
        <LedgerTable
          cell={cell}
          colConfig={config}
          rows={rows}
          addRow={addRow}
          deleteRow={deleteRow}
          cells={ledgerCells}
          setCells={setLedgerCells}
          disabled={disabledCondition}
          tableTerms={{
            tableName: 'Cash Distribution',
            tableSlug: 'cash-distribution-ledger',
            columnName: 'Cash Distribution',
            pluralColumnName: 'Cash Distributions',
          }}
          isAlertVisible={isAlertVisible}
          setIsAlertVisible={setIsAlertVisible}
          currency={format.currency}
          currencyCode={format.currency.code}
          exchangeRateMap={exchangeRateMap}
          linkedCellUpdates={linkedCellChanges}
        />
      )}
    </LedgerDialog>
  );
};

export default MultipleInvestmentDates;
